import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { Selector } from '@/components/ui/inputs/selector';
import Table from '@/components/ui/tables/Table';
import { useEmployeeContractList } from '@/hooks/employee/useEmployeeContractList';
import { useTranslation } from '@/i18n';
import { PermissionGuard } from '@/components/layout';


export const EmployeeContractList: React.FC = () => {
  const { t: translate } = useTranslation();
  
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    selectedEmployeeId,
    handleEmployeeChange,
    createContractUrl,
    dynamicTitle,
    employeeSelectorProps,
    filters,
    setFilters,
    filterConfig,
  } = useEmployeeContractList();

  // Build the employee Selector with pagination as extraFilters
  const employeeSelector = (
    <Selector
      label={translate('employee:fields.employee')}
      placeholder={translate('employee:contracts.selectEmployee')}
      value={selectedEmployeeId}
      onChange={(value) => handleEmployeeChange(value as string)}
      options={employeeSelectorProps.options}
      onLoadMore={employeeSelectorProps.onLoadMore}
      hasNextPage={employeeSelectorProps.hasNextPage}
      isLoadingMore={employeeSelectorProps.isLoadingMore}
      onSearchChange={employeeSelectorProps.onSearchChange}
      searchable
      data-testid="employee-selector"
    />
  );

  return (
    <PageLayout dynamicTitle={dynamicTitle}>
      <div className="flex flex-col gap-6" data-testid="employee-contract-list-page">
        <div>
          <PermissionGuard action="create">
            <Link to={createContractUrl}>
              <PrimaryButton 
                iconLeft={<IconScout name="plus" />}
                disabled={!selectedEmployeeId}
              >
                {translate('employee:contracts.create')}
              </PrimaryButton>
            </Link>
          </PermissionGuard>
        </div>
        <div className="flex flex-wrap gap-4 justify-between items-start">
          <FilterDropdown
            extraFilters={employeeSelector}
            filters={filterConfig}
            values={{ contractStatusFilter: filters.contractStatusFilter }}
            onChangeFilter={(key, value) => {
              setFilters(prev => ({
                ...prev,
                [key]: value,
              }));
            }}
          />
        </div>

        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          searchable
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          columnMaxWidth={150}
          initialSort={{ key: 'contractNumber', direction: 'asc' }}
          actions={actions}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="contract-table"
          isLoading={loading}
        />
      </div>
    </PageLayout>
  );
};

export default EmployeeContractList;
